import { z } from 'zod';

/**
 * Notification type registry (#304).
 *
 * A single source of truth — shared by the API's `notify()` facade and the web
 * settings UI — for every notification the portal can emit: its human label, the
 * channels it may use, and each channel's default on/off state. Storing the
 * registry here (not in the API) lets the preferences UI render toggles without a
 * round-trip and guarantees the two sides never disagree about which types exist.
 *
 * User overrides are stored SPARSELY (only the deltas from these defaults), so the
 * blob stays tiny even as the registry grows — see `resolveChannels`.
 */

export const NOTIFICATION_CHANNELS = ['inApp', 'email', 'teams'] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];

export const NOTIFICATION_CATEGORIES = [
  'approvals',
  'finance',
  'projects',
  'safety',
  'security',
] as const;
export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number];

export const NOTIFICATION_CATEGORY_LABELS: Record<NotificationCategory, string> = {
  approvals: 'Approvals',
  finance: 'Finance',
  projects: 'Projects',
  safety: 'Safety',
  security: 'Security & Ops',
};

export type ChannelState = Record<NotificationChannel, boolean>;

export interface NotificationTypeDef {
  /** Stable key — matches the `type` column on the Notification row. */
  key: string;
  label: string;
  description: string;
  category: NotificationCategory;
  /** Default on/off per channel before any user override. */
  defaultChannels: ChannelState;
  /**
   * Channels the user cannot turn off (always delivered) — used for high-signal
   * events like safety incidents where an in-app record must always land.
   */
  lockedChannels?: NotificationChannel[];
  /** Eligible to be batched into a per-user digest instead of emailed instantly. */
  digestable?: boolean;
  /** Assignee-targeted — always sent instantly, never digested. */
  direct?: boolean;
}

const off: ChannelState = { inApp: false, email: false, teams: false };

function def(
  key: string,
  category: NotificationCategory,
  label: string,
  description: string,
  channels: Partial<ChannelState>,
  opts?: { lockedChannels?: NotificationChannel[]; digestable?: boolean; direct?: boolean },
): NotificationTypeDef {
  return {
    key,
    label,
    description,
    category,
    defaultChannels: { ...off, ...channels },
    ...(opts?.lockedChannels ? { lockedChannels: opts.lockedChannels } : {}),
    ...(opts?.digestable ? { digestable: true } : {}),
    ...(opts?.direct ? { direct: true } : {}),
  };
}

/**
 * The registry. In-app is the baseline channel for everything; email is on by
 * default only where the portal already sends it today (transactional PO / sub
 * invoice updates) plus the two silent leadership events this issue adds. Teams
 * is opt-in per type and additionally gated by `TEAMS_WEBHOOK_URL` in the API.
 */
export const NOTIFICATION_TYPES: readonly NotificationTypeDef[] = [
  // ---- Safety ----
  def(
    'safety_incident',
    'safety',
    'Safety incident reported',
    'A safety incident was filed. Recordable-or-worse incidents alert leadership immediately.',
    { inApp: true, email: true, teams: true },
    { lockedChannels: ['inApp'] },
  ),
  def(
    'cert_expiring',
    'safety',
    'Certification expiring',
    'A team member’s safety certification is expiring soon or has lapsed.',
    { inApp: true },
  ),
  // ---- Approvals ----
  def(
    'approval_pending',
    'approvals',
    'Purchase request awaiting approval',
    'A purchase request is waiting on your PM or Ops approval.',
    { inApp: true },
  ),
  def(
    'po_request_queued',
    'approvals',
    'PO request ready to issue',
    'A purchase-order request reached the purchasing queue and is awaiting issue.',
    { inApp: true },
  ),
  def(
    'po_request_submitted',
    'approvals',
    'PO request submitted',
    'A purchase-order request was submitted from the portal. Over-budget requests need ops approval before they are final.',
    { inApp: true },
  ),
  def(
    'po_request_issued',
    'finance',
    'Your PO request was issued',
    'A PO request you submitted was issued to ConnectWise.',
    { inApp: true, email: true },
    { direct: true },
  ),
  def(
    'po_request_rejected',
    'finance',
    'Your PO request was rejected',
    'A PO request you submitted was rejected by purchasing.',
    { inApp: true, email: true },
    { direct: true },
  ),
  // ---- Finance (sub invoices) ----
  def(
    'sub_invoice_pm_approved',
    'finance',
    'Sub invoice — PM approved',
    'A subcontractor invoice cleared project-manager review.',
    { inApp: true, email: true },
    { digestable: true },
  ),
  def(
    'sub_invoice_ops_approved',
    'finance',
    'Sub invoice — approved for payment',
    'A subcontractor invoice was fully approved and queued for payment.',
    { inApp: true, email: true },
    { digestable: true },
  ),
  def(
    'sub_invoice_rejected',
    'finance',
    'Sub invoice — rejected',
    'A subcontractor invoice was rejected.',
    { inApp: true, email: true },
    { digestable: true },
  ),
  def(
    'sub_invoice_paid',
    'finance',
    'Sub invoice — paid',
    'A subcontractor invoice was paid.',
    { inApp: true, email: true },
    { digestable: true },
  ),
  def(
    'sub_invoice_ocr_submitted',
    'finance',
    'Invoice processed and submitted',
    'An invoice a subcontractor uploaded while the document reader was down was read automatically and submitted for review.',
    { inApp: true, email: true },
    { digestable: true },
  ),
  def(
    'sub_invoice_ocr_failed',
    'finance',
    'Invoice could not be processed',
    'An uploaded invoice could not be read after repeated attempts — needs manual handling.',
    { inApp: true },
    { direct: true },
  ),
  def(
    'receiving_sla',
    'finance',
    'Approved sub invoice not received',
    'A fully-approved sub invoice has not been marked received past its SLA — payment sync is blocked.',
    { inApp: true },
  ),
  def(
    'forecast_overrun',
    'projects',
    'Projected budget overrun',
    'A project is forecast to exceed its labor budget before it happens.',
    { inApp: true },
  ),
  // ---- Projects ----
  def(
    'kickoff_sla',
    'projects',
    'Kickoff overdue',
    'A project kickoff has been in progress past its SLA.',
    { inApp: true },
  ),
  // ---- Security & Ops ----
  def(
    'secret_expiring',
    'security',
    'Secret expiring / rotation due',
    'A Key Vault secret is nearing its expiry or is overdue for rotation. Alerts leadership to rotate it.',
    { inApp: true, email: true, teams: true },
    { lockedChannels: ['inApp'] },
  ),
  def(
    'coi_expiring',
    'security',
    'Subcontractor COI expiring',
    "A subcontractor's insurance certificate is expired or expiring soon — renew it before dispatching them.",
    { inApp: true, email: true },
    // Digestable so leadership sees all expiring/expired COIs in a single batched
    // email per digest window instead of one email per (sub, coverage, date)
    // tuple. Critical-severity (just-expired) still short-circuits to instant via
    // the digest-router.
    { digestable: true },
  ),
  def(
    'coi_renewal_submitted',
    'security',
    'Subcontractor COI renewal submitted',
    'A subcontractor uploaded a renewed insurance certificate awaiting review.',
    { inApp: true, email: false },
    // In-app only, deliberately. "Always review" must not become a per-renewal
    // inbox item — the review workload lives on the queue page with a badge
    // count, and the weekly digest carries the aggregate.
    { digestable: false },
  ),
  def(
    'coi_ladder',
    'security',
    'Subcontractor COI renewal reminder',
    'Reminds a subcontractor that an insurance certificate needs renewing.',
    { inApp: false, email: true },
    // Direct, never digestable: this goes to an EXTERNAL subcontractor who has no
    // portal digest settings, and batching a compliance deadline is wrong anyway.
    { digestable: false, direct: true },
  ),
  def(
    'coi_weekly_digest',
    'security',
    'Weekly subcontractor COI digest',
    'One weekly summary of lapsed certificates and renewals awaiting review.',
    { inApp: false, email: true },
    { digestable: false, direct: true },
  ),
  def(
    'coi_esc_dispatch_blocked',
    'security',
    'COI blocked a dispatch',
    'A subcontractor could not be scheduled because their insurance has lapsed.',
    { inApp: true, email: true },
    { digestable: false, direct: true },
  ),
  def(
    'coi_esc_disengaged',
    'security',
    'Subcontractor ignoring COI reminders',
    'A subcontractor has been lapsed for weeks without opening a single reminder.',
    { inApp: true, email: true },
    { digestable: false, direct: true },
  ),
  def(
    'coi_esc_review_overdue',
    'security',
    'COI renewal waiting on review',
    'A subcontractor filed a certificate and nobody has reviewed it.',
    { inApp: true, email: true },
    { digestable: false, direct: true },
  ),
  def(
    'coi_esc_bounced',
    'security',
    'COI reminder bounced',
    'Reminder email to a subcontractor is undeliverable, so every rung is firing into a void.',
    { inApp: true, email: true },
    { digestable: false, direct: true },
  ),
  def(
    'time_sync_stranded',
    'security',
    'Field time entry stranded (not synced to CW)',
    'A field-visit time entry failed to post to ConnectWise and has been stuck in needs_sync past the alert threshold. Payroll/billing read hours from CW, so a stranded row silently drops paid/billable hours until it is re-posted.',
    { inApp: true, email: true, teams: true },
    { lockedChannels: ['inApp'] },
  ),
  def(
    'account_activity_note_stranded',
    'security',
    'Account activity not mirrored to CW',
    'A Program Manager account-activity handoff failed to mirror to its ConnectWise company note and has sat un-mirrored (cwNoteId is null) past the alert threshold. The activity is invisible in CW until the backfill re-posts it.',
    { inApp: true, email: true, teams: true },
    { lockedChannels: ['inApp'] },
  ),
  def(
    'packing_pdf_upload_stranded',
    'security',
    'Delivery-receipt PDF not uploaded to CW',
    'A signed packing-list delivery receipt PDF was generated (it survives in blob storage) but its upload to the ConnectWise project record failed and has stayed un-uploaded (cwDocumentId is null) past the alert threshold. The CW project is silently missing the receipt until the reconcile job re-uploads it.',
    { inApp: true, email: true, teams: true },
    { lockedChannels: ['inApp'] },
  ),
  def(
    'queue_overdue',
    'projects',
    'Work queue overdue',
    'An item in a work queue (billing, change orders, packing lists, tasks…) breached its SLA.',
    { inApp: true },
  ),
  def(
    'project.execution_complete',
    'projects',
    'Project execution complete',
    'All tasks and checklists are done for a project.',
    { inApp: true },
  ),
  def(
    'project.new',
    'projects',
    'New project created',
    'A project reached "01. New" in ConnectWise — kickoff can begin. Replaces CW wf75 (internal). One notification to the assigned PM.',
    { inApp: true, email: true },
    { digestable: true },
  ),
  def(
    'project.review_pending',
    'projects',
    'Project ready for review',
    'A project reached "02. PM Review" in ConnectWise — ready for the PM to review. Replaces CW wf78 "Job Review Pending".',
    { inApp: true, email: true },
    { digestable: true },
  ),
  def(
    'project.completed',
    'projects',
    'Project closed',
    'A project reached "12. Closed" in ConnectWise. Signals completion for downstream proactive comms (slice 5).',
    { inApp: true, email: true },
    { digestable: true },
  ),
  def(
    'project.weekly_update_due',
    'projects',
    'Weekly customer update due',
    'A weekly customer update is ready to send for one of your active projects. Open it from your My Tasks list to review and send.',
    { inApp: true },
    { digestable: true },
  ),
  // ---- Tasks & dispatch (assignee-targeted; #304 follow-up) ----
  def(
    'task_assigned',
    'projects',
    'Task assigned to you',
    'Someone assigned you a new task.',
    { inApp: true, email: true },
    { direct: true },
  ),
  def(
    'task_due',
    'projects',
    'Task due soon or overdue',
    'A task assigned to you is due within a day or is now overdue.',
    { inApp: true, email: true },
    { direct: true },
  ),
  def(
    'dispatch_assigned',
    'projects',
    'You were dispatched',
    'You were scheduled onto a ticket in dispatch.',
    { inApp: true, email: true },
    { direct: true },
  ),
  def(
    'overdue_visit',
    'projects',
    'Onsite visit still open',
    'A field visit has been open far longer than expected and is accruing time — close it or it will over-bill.',
    { inApp: true, email: true },
    { direct: true },
  ),
] as const;

const TYPE_BY_KEY: Map<string, NotificationTypeDef> = new Map(
  NOTIFICATION_TYPES.map((t) => [t.key, t]),
);

export function getNotificationType(key: string): NotificationTypeDef | undefined {
  return TYPE_BY_KEY.get(key);
}

/** True if a type is eligible for digest batching. */
export function isDigestable(typeKey: string): boolean {
  return TYPE_BY_KEY.get(typeKey)?.digestable === true;
}
/** True if a type is assignee-targeted and must always send instantly. */
export function isDirect(typeKey: string): boolean {
  return TYPE_BY_KEY.get(typeKey)?.direct === true;
}

/** Sparse per-user overrides: `{ [typeKey]: { email: false } }`. */
export type NotificationPreferences = Record<string, Partial<ChannelState>>;

/**
 * Resolve the effective channel state for a type given a user's sparse overrides.
 *
 * Precedence (low → high): registry defaults, then user overrides, then locked
 * channels (always on). Unknown types — e.g. a notification created before it was
 * added to the registry — fall back to in-app only so they are never silently
 * dropped and never gain email/Teams by accident.
 */
export function resolveChannels(
  typeKey: string,
  prefs: NotificationPreferences | null | undefined,
): ChannelState {
  const type = TYPE_BY_KEY.get(typeKey);
  if (!type) return { inApp: true, email: false, teams: false };

  const override = prefs?.[typeKey] ?? {};
  const resolved: ChannelState = {
    inApp: override.inApp ?? type.defaultChannels.inApp,
    email: override.email ?? type.defaultChannels.email,
    teams: override.teams ?? type.defaultChannels.teams,
  };
  for (const locked of type.lockedChannels ?? []) resolved[locked] = true;
  return resolved;
}

/** Convenience: is one channel enabled for a type given the user's prefs? */
export function isChannelEnabled(
  typeKey: string,
  channel: NotificationChannel,
  prefs: NotificationPreferences | null | undefined,
): boolean {
  return resolveChannels(typeKey, prefs)[channel];
}

/**
 * Zod schema for a preferences PUT payload. Only known type keys and known
 * channels are accepted; locked channels are rejected so the client can't try to
 * disable an always-on channel. Unknown keys are stripped rather than erroring so
 * a stale client can still save the keys it does know.
 */
export const notificationPreferencesSchema = z
  .record(
    z.string(),
    z
      .object({
        inApp: z.boolean().optional(),
        email: z.boolean().optional(),
        teams: z.boolean().optional(),
      })
      .strict(),
  )
  .transform((raw) => {
    const clean: NotificationPreferences = {};
    for (const [key, channels] of Object.entries(raw)) {
      const type = TYPE_BY_KEY.get(key);
      if (!type) continue; // drop unknown types
      const locked = new Set(type.lockedChannels ?? []);
      const entry: Partial<ChannelState> = {};
      for (const ch of NOTIFICATION_CHANNELS) {
        const v = channels[ch];
        if (v === undefined || locked.has(ch)) continue; // can't override a locked channel
        entry[ch] = v;
      }
      if (Object.keys(entry).length > 0) clean[key] = entry;
    }
    return clean;
  });
